import { SITE_NAME, SITE_URL } from './site';

export type ObjectToolSlug =
  | 'json-formatter'
  | 'json-minifier'
  | 'object-keys'
  | 'object-values'
  | 'object-entries'
  | 'sort-object-keys'
  | 'flatten-object'
  | 'object-to-query-string'
  | 'object-diff'
  | 'merge-objects';

export type ObjectToolCategory = 'format' | 'extract' | 'transform' | 'compare';

export interface ObjectToolRoute {
  slug: ObjectToolSlug;
  category: ObjectToolCategory;
  label: string;
  shortLabel: string;
  title: string;
  description: string;
}

export const OBJECT_TOOL_CATEGORY_LABELS: Record<ObjectToolCategory, string> = {
  format: 'Format',
  extract: 'Extract',
  transform: 'Transform',
  compare: 'Compare',
};

export const OBJECT_TOOL_ROUTES: ObjectToolRoute[] = [
  {
    slug: 'json-formatter',
    category: 'format',
    label: 'JSON Formatter',
    shortLabel: 'Formatter',
    title: 'JSON Formatter Online',
    description:
      'Format JavaScript objects and JSON online with readable indentation for debugging, docs, and API responses.',
  },
  {
    slug: 'json-minifier',
    category: 'format',
    label: 'JSON Minifier',
    shortLabel: 'Minifier',
    title: 'JSON Minifier Online',
    description:
      'Minify JavaScript objects and JSON online into compact one-line output for payloads, configs, and tests.',
  },
  {
    slug: 'object-keys',
    category: 'extract',
    label: 'Object Keys Extractor',
    shortLabel: 'Keys',
    title: 'Object Keys Extractor Online',
    description:
      'Extract object keys online from a JavaScript object or JSON object and copy them as a JSON array.',
  },
  {
    slug: 'object-values',
    category: 'extract',
    label: 'Object Values Extractor',
    shortLabel: 'Values',
    title: 'Object Values Extractor Online',
    description:
      'Extract object values online from a JavaScript object or JSON object with formatted JSON output.',
  },
  {
    slug: 'object-entries',
    category: 'extract',
    label: 'Object Entries Converter',
    shortLabel: 'Entries',
    title: 'Object Entries Converter Online',
    description:
      'Convert an object to Object.entries-style key/value pairs online for JavaScript development.',
  },
  {
    slug: 'sort-object-keys',
    category: 'transform',
    label: 'Sort Object Keys',
    shortLabel: 'Sort keys',
    title: 'Sort Object Keys Online',
    description:
      'Sort object keys alphabetically online and copy stable formatted JSON for tests, snapshots, and configs.',
  },
  {
    slug: 'flatten-object',
    category: 'transform',
    label: 'Flatten Object',
    shortLabel: 'Flatten',
    title: 'Flatten Object Online',
    description:
      'Flatten nested JavaScript objects online into dot-notation keys for logs, forms, analytics, and config work.',
  },
  {
    slug: 'object-to-query-string',
    category: 'transform',
    label: 'Object to Query String',
    shortLabel: 'Query string',
    title: 'Object to Query String Converter Online',
    description:
      'Convert a JavaScript object to a URL query string online for API calls, filters, and frontend routing.',
  },
  {
    slug: 'object-diff',
    category: 'compare',
    label: 'Object Diff Checker',
    shortLabel: 'Diff',
    title: 'Object Diff Checker Online',
    description:
      'Compare two JavaScript objects online and find added, removed, and changed values using dot-notation paths.',
  },
  {
    slug: 'merge-objects',
    category: 'compare',
    label: 'Merge Objects',
    shortLabel: 'Merge',
    title: 'Merge Objects Online',
    description:
      'Merge two JavaScript objects online with the second object overriding matching keys from the first object.',
  },
];

export function isObjectToolSlug(value: string): value is ObjectToolSlug {
  return OBJECT_TOOL_ROUTES.some((route) => route.slug === value);
}

export function getObjectToolRoute(slug: string | undefined): ObjectToolRoute | null {
  if (!slug || !isObjectToolSlug(slug)) {
    return null;
  }

  return OBJECT_TOOL_ROUTES.find((route) => route.slug === slug) ?? null;
}

export function getObjectToolPath(slug: ObjectToolSlug): string {
  return `/javascript-object-tools/${slug}`;
}

export function getObjectToolsByCategory(category: ObjectToolCategory): ObjectToolRoute[] {
  return OBJECT_TOOL_ROUTES.filter((route) => route.category === category);
}

export function getObjectToolStructuredData(route: ObjectToolRoute) {
  const path = getObjectToolPath(route.slug);
  const url = `${SITE_URL}${path}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: route.title,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Any',
      url,
      description: route.description,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: SITE_NAME,
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: route.shortLabel,
          item: url,
        },
      ],
    },
  ];
}
